(() => {
  'use strict';

  const cardSelector = '.ProductItem, [data-product-card-discount-scope]';
  const dataSelector = 'script[data-product-card-discount-data]';
  const queued = new Set();
  const versions = new WeakMap();
  const completed = new WeakSet();
  const observed = new WeakSet();
  let api;
  let visibilityObserver;
  let scheduled = false;

  const presentClubPrice = (card) => {
    const label = card?.dataset.productCardClubLabel;
    if (!label) return;
    const description = card.querySelector('price-list .regios-dopp-description');
    const text = description?.textContent.trim();
    const isClub = !!text && (/^CENA DLA KLUBOWICZ(?:A|ÓW)(?: XL)?$/iu.test(text)
      || (description.hasAttribute('data-product-card-club-description') && text === label));
    if (isClub) {
      description.setAttribute('data-product-card-club-description', '');
      if (description.textContent !== label) description.textContent = label;
    }
    const fallback = card.querySelector('[data-product-card-club-price]');
    if (fallback) fallback.hidden = isClub && description.getClientRects().length > 0;
  };

  const updateCard = async (card) => {
    if (!card.isConnected || completed.has(card)) return;
    const data = card.querySelector(dataSelector);
    const price = card.querySelector('.regios-dopp-generic-price-container');
    if (!data || !price || !price.getClientRects().length) return;

    try {
      const product = JSON.parse(data.textContent);
      const version = versions.get(card) || 0;
      if (!product.productId || !product.variantId || !Number.isFinite(product.regularPriceInCents)) return;
      card.dataset.productCardDiscountState = 'updating';
      // The official renderer retains the active market, customer and discount rules.
      await api.themeInterop.updateOnPagePrices(card, 'COLLECTION_PAGE', product);
      presentClubPrice(card);
      if ((versions.get(card) || 0) !== version) { queued.add(card); return; }
      if (card.isConnected) {
        completed.add(card);
        card.dataset.productCardDiscountState = 'ready';
      }
    } catch {
      // Preserve the server price when the app is unavailable; never calculate a fallback discount.
      card.dataset.productCardDiscountState = 'error';
    }
  };

  const flush = async () => {
    if (scheduled || !api) return;
    scheduled = true;
    while (queued.size) {
      const batch = [...queued].slice(0, 4);
      batch.forEach(card => queued.delete(card));
      await Promise.all(batch.map(updateCard));
    }
    scheduled = false;
  };

  const enqueue = (card) => {
    if (!card?.matches(cardSelector) || completed.has(card) || !card.querySelector(dataSelector)) return;
    if (visibilityObserver && !observed.has(card)) {
      observed.add(card);
      visibilityObserver.observe(card);
    }
    if (!api) return;
    queued.add(card);
    void flush();
  };

  const scan = (root) => {
    if (root.nodeType !== Node.ELEMENT_NODE && root !== document) return;
    if (root.matches?.(dataSelector)) enqueue(root.closest(cardSelector));
    if (root.matches?.(cardSelector)) enqueue(root);
    root.querySelectorAll(dataSelector).forEach(data => enqueue(data.closest(cardSelector)));
  };

  const connect = (candidate) => {
    if (typeof candidate?.themeInterop?.updateOnPagePrices !== 'function') return;
    api = candidate;
    scan(document);
    void flush();
  };

  window.addEventListener('regios-dopp:api-initialized', event => connect(event.detail?.api));
  document.addEventListener('product-card:changed', event => scan(event.detail?.card || document));
  document.addEventListener('product-card:price-variant', event => {
    const scope = event.detail?.scope;
    const data = scope?.querySelector(dataSelector);
    if (!data || !scope.matches('[data-product-card-discount-scope]')) return;
    try {
      const product = JSON.parse(data.textContent);
      const variant = product.variants.find(item => String(item.id) === String(event.detail.variantId));
      if (!variant) return;
      product.variantId = variant.id;
      product.regularPriceInCents = variant.priceInCents;
      product.compareAtPriceInCents = variant.compareAtPriceInCents;
      data.textContent = JSON.stringify(product);
      versions.set(scope, (versions.get(scope) || 0) + 1);
      completed.delete(scope);
      const currency = window.Shopify.currency.active;
      const label = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency }).format(variant.priceInCents / 100);
      const price = scope.querySelector('.UiPrice__current sale-price');
      if (price) price.textContent = label;
      enqueue(scope);
    } catch { scope.dataset.productCardDiscountState = 'error'; }
  });
  document.addEventListener('shopify:section:load', event => scan(event.target));

  const start = () => {
    // Regios skips hidden price containers. A hidden tab or responsive copy is
    // eligible for its first calculation only after becoming visible.
    visibilityObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) enqueue(entry.target);
      });
    });
    // Observe inserted cards only. Regios price mutations must not trigger another calculation.
    new MutationObserver(records => {
      records.forEach(record => {
        record.addedNodes.forEach(scan);
        const card = record.target.parentElement?.closest(cardSelector);
        if (card) presentClubPrice(card);
      });
    }).observe(document.body, { childList: true, subtree: true });
    scan(document);
    connect(window.RegiosDOPP?.api?.v0);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
